import { useQuery } from 'react-query';
import axios from 'axios';
import { getUserDistricts, UserDistrictsResponse } from '@apis/districtReports';
import { fetchDistrictCurriculumExams } from '@apis/learnosityexams';
import { getDistrictAdminCurriculumList } from '@apis/curriculum';
import { ILEExamSession } from '@components/Component/LearnosityExams/types';
import mapTestSessions from '@components/Component/LearnosityExams/utils/mapTestSessions';
import { MappedTestSession } from '@components/Component/LearnosityExams/types';

export interface CurriculumSubject {
  id: number;
  display_name: string;
  public: boolean;
}

export interface Curriculum {
  id: number;
  name: string;
  slug: string;
  public: boolean;
  has_exam: boolean;
  available_subjects: CurriculumSubject[];
  coauthor_enabled: boolean;
  curriculum_type: number;
  adaptive_learnosity_practice_enabled?: boolean;
  learnosity_lesson_review_enabled?: boolean;
}

/**
 * Hook to fetch available accessible to district admins curriculums
 * Uses the getDistrictAdminCurriculumList API function
 */
export const useCurriculums = (districtId: number | undefined) => {
  return useQuery<Curriculum[], Error>(
    ['districtAdminCurriculums', districtId],
    async () => {
      const { response, error } = (await getDistrictAdminCurriculumList(districtId)) as {
        response?: { data: Curriculum[] };
        error?: Error;
      };
      if (error) throw error;
      return response?.data || [];
    },
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000,
      enabled: !!districtId,
    },
  );
};

/**
 * Hook to get the current admin user's district with curriculum options
 * Uses the dedicated admin district endpoint
 */
export const useUserDistricts = () => {
  const { data, isLoading, error } = useQuery<UserDistrictsResponse, Error>(
    ['adminDistrict'],
    getUserDistricts,
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000,
      retry: 1,
    },
  );

  return {
    districts: data?.districts || [],
    isLoading,
    error,
  };
};

/**
 * Hook to fetch available subjects for a curriculum
 */
export const useCurriculumSubjects = (curriculumId: number | undefined) => {
  return useQuery<CurriculumSubject[], Error>(
    ['curriculumSubjects', curriculumId],
    async () => {
      if (!curriculumId) {
        return [];
      }
      const url = `/api/curriculum/${curriculumId}/available-subjects/`;
      const response = await axios.get(url);
      return response.data || [];
    },
    {
      enabled: !!curriculumId,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000,
    },
  );
};

export interface SelectedSubject {
  id: number;
  name: string;
  curriculumId: number;
}

/**
 * Hook to fetch district exam sessions
 */
export const useDistrictCurriculumExams = (
  districtId: number | undefined,
  curriculumId: number | undefined,
) => {
  return useQuery<MappedTestSession[], Error>(
    ['districtCurriculumExams', districtId, curriculumId],
    async () => {
      if (!districtId || !curriculumId) {
        return [];
      }
      const response = (await fetchDistrictCurriculumExams(
        districtId,
        curriculumId,
      )) as unknown as {
        response: {
          data: ILEExamSession[];
        };
      };
      const examSessions = response?.response?.data || [];
      return mapTestSessions(examSessions) || [];
    },
    {
      enabled: !!districtId && !!curriculumId,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000,
    },
  );
};

export interface SelectedExam {
  id: number;
  examName: string;
  examType: string;
  examDate: string;
  examId: number; // The exam.id from the exam session
}
